import { useCallback, useEffect, useState } from 'react'

import { axiosInstance } from '../../../../../hooks/useAxios'
import { IListSaleOrdersResult, ISaleOrderListItem } from '../types'

interface Params {
  enabled: boolean // ดึงเฉพาะตอน modal เปิด
  status?: string
  startDate?: string
  endDate?: string
  shopId?: string
  by?: 'order' | 'settlement'
  paymentType?: 'cod' | 'transfer' // กรองประเภทชำระเงิน (ไม่ส่ง = ทุกประเภท)
  costMissing?: boolean // true = เฉพาะออเดอร์ที่ต้นทุนยังไม่ครบ
  reloadKey?: number // เปลี่ยนค่า = บังคับโหลดใหม่จากหน้าแรก (เช่น หลัง backfill เสร็จ)
}

export const PAGE_SIZE = 20

// list sale_orders แบบ cursor keyset ทีละหน้า (ก่อนหน้า/ถัดไป)
// เก็บ stack ของ cursor ต่อหน้า (index 0 = undefined = หน้าแรก) เพื่อถอยหลังได้
export const useSaleOrders = ({
  enabled,
  status,
  startDate,
  endDate,
  shopId,
  by,
  paymentType,
  costMissing,
  reloadKey,
}: Params) => {
  const [items, setItems] = useState<ISaleOrderListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)

  const fetchPage = useCallback(
    async (cursor?: string) => {
      setIsLoading(true)
      setError(false)
      try {
        const res = await axiosInstance.get<IListSaleOrdersResult>(
          '/tiktok/report/orders/list',
          {
            params: {
              ...(status ? { status } : {}),
              ...(startDate ? { startDate } : {}),
              ...(endDate ? { endDate } : {}),
              ...(shopId ? { shopId } : {}),
              ...(by ? { by } : {}),
              ...(paymentType ? { paymentType } : {}),
              ...(costMissing ? { costMissing: true } : {}),
              ...(cursor ? { cursor } : {}),
              limit: PAGE_SIZE,
            },
          },
        )
        setItems(res.data.items)
        setNextCursor(res.data.nextCursor)
      } catch {
        setError(true)
      } finally {
        setIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      status,
      startDate,
      endDate,
      shopId,
      by,
      paymentType,
      costMissing,
      reloadKey,
    ],
  )

  // reset ไปหน้าแรกเมื่อเปิด modal หรือ filter เปลี่ยน
  useEffect(() => {
    if (!enabled) {
      return
    }
    setCursors([undefined])
    setPageIndex(0)
    setItems([])
    setNextCursor(null)
    fetchPage()
  }, [enabled, fetchPage])

  const goNext = () => {
    if (!nextCursor || isLoading) {
      return
    }
    // ตัด stack ให้เหลือถึงหน้าปัจจุบัน แล้วต่อ cursor หน้าถัดไป (กันซ้ำตอนถอยแล้วเดินใหม่)
    setCursors((prev) => [...prev.slice(0, pageIndex + 1), nextCursor])
    setPageIndex((prev) => prev + 1)
    fetchPage(nextCursor)
  }

  const goPrev = () => {
    if (pageIndex === 0 || isLoading) {
      return
    }
    const target = cursors[pageIndex - 1]
    setPageIndex((prev) => prev - 1)
    fetchPage(target)
  }

  return {
    items,
    isLoading,
    error,
    pageIndex,
    startIndex: pageIndex * PAGE_SIZE, // ฐานลำดับของหน้าปัจจุบัน
    hasNext: !!nextCursor,
    hasPrev: pageIndex > 0,
    goNext,
    goPrev,
  }
}
